import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { OnyxLogo } from "./Brand";
import { api, fileUrl } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/configurator", label: "Estimate" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => r.data.logo && setLogo(fileUrl(r.data.logo))).catch(() => {});
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${solid ? "bg-onyx-cream/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo">
            <OnyxLogo logo={logo} dark={!solid} className={logo ? `h-9 transition-[filter] duration-500 ${solid ? "" : "invert"}` : ""} />
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-[13px] tracking-wide transition-colors ${solid ? "text-onyx-charcoal hover:text-onyx-wood" : "text-white/80 hover:text-white"} ${pathname === l.to ? "font-semibold" : "font-normal"}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-request-proposal"
              className={`text-[12px] tracking-[0.12em] uppercase px-6 py-3 transition-colors ${solid ? "bg-onyx-black text-white hover:bg-onyx-wood hover:text-onyx-black" : "bg-white text-onyx-black hover:bg-onyx-wood"}`}
            >
              Request a Proposal
            </Link>
          </nav>
          <button data-testid="nav-mobile-toggle" className={`md:hidden ${solid ? "text-onyx-black" : "text-white"}`} onClick={() => setOpen(true)}>
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-onyx-black text-white flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
              <OnyxLogo logo={logo} dark className={logo ? "h-9" : ""} />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)}><X size={26} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <Link to={l.to} className="font-display text-4xl font-bold tracking-tight py-2 block">{l.label}</Link>
                </motion.div>
              ))}
              <Link to="/contact" className="mt-8 bg-white text-onyx-black text-center py-4 text-sm tracking-[0.15em] uppercase">Request a Proposal</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
